<template>
  <div class="p-3">

    <!-- Header row with back button -->
    <div class="d-flex align-items-center mb-4">
      <h5 class="mb-0 font-weight-bold">{{ isEditMode ? 'Edit End Semester Exam' : 'Create End Semester Exam' }}</h5>
    </div>

    <div v-if="errorMessage" class="text-danger small mb-3">{{ errorMessage }}</div>

    <div class="loading" v-if="fetchDropdowns">
      <load-data></load-data>
    </div>

    <b-form v-else @submit.prevent="submitForm" novalidate>
      <div class="coe-form-card">

        <!-- ===== SECTION 1: Basic Details ===== -->
        <div class="coe-section-header">
          <i class="fas fa-info-circle mr-2 text-primary"></i>
          Exam Details
        </div>
        <hr class="coe-section-divider" />

        <b-row>
          <!-- Exam Name -->
          <b-col cols="12" md="6">
            <b-form-group
              label="Exam Name"
              label-class="font-weight-bold required-label"
            >
              <b-form-input
                v-model="form.exam_name"
                placeholder="e.g. ESE May 2026"
                :state="validated ? form.exam_name.trim().length > 0 : null"
                trim
              />
              <b-form-invalid-feedback>Exam name is required.</b-form-invalid-feedback>
            </b-form-group>
          </b-col>

          <!-- Exam Type -->
          <b-col cols="12" md="6">
            <b-form-group
              label="Exam Type"
              label-class="font-weight-bold required-label"
            >
              <b-form-select
                v-model="form.exam_type"
                :options="examTypeOptions"
                :state="validated ? form.exam_type !== null : null"
              />
              <b-form-invalid-feedback>Please select an exam type.</b-form-invalid-feedback>
            </b-form-group>
          </b-col>

          <!-- Class -->
          <b-col cols="12" md="6">
            <b-form-group
              label="Class"
              label-class="font-weight-bold required-label"
            >
              <b-form-select
                v-model="form.class_id"
                :options="classOptions"
                :state="validated ? form.class_id !== null : null"
              />
              <b-form-invalid-feedback>Please select a class.</b-form-invalid-feedback>
            </b-form-group>
          </b-col>

          <!-- Scheme -->
          <b-col cols="12" md="6">
            <b-form-group
              label="Scheme / Regulation"
              label-class="font-weight-bold required-label"
            >
              <b-form-select
                v-model="form.scheme"
                :options="schemeOptions"
                :state="validated ? form.scheme !== null : null"
              />
              <b-form-invalid-feedback>Please select a scheme.</b-form-invalid-feedback>
            </b-form-group>
          </b-col>

          <!-- Academic Year -->
          <b-col cols="12" md="6">
            <b-form-group
              label="Academic Year"
              label-class="font-weight-bold required-label"
            >
              <b-form-select
                v-model="form.academic_year"
                :options="academicYearSemesterOptions"
                :state="validated ? form.academic_year !== null : null"
              />
              <b-form-invalid-feedback>Academic year is required.</b-form-invalid-feedback>
            </b-form-group>
          </b-col>

          <!-- Exam Month & Year -->
          <b-col cols="12" md="6">
            <b-form-group
              label="Exam Month & Year"
              label-class="font-weight-bold required-label"
            >
              <b-form-input
                type="month"
                v-model="form.exam_period"
                :state="validated ? !!form.exam_period : null"
              />
              <b-form-invalid-feedback>Please select a month and year.</b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- ===== SECTION 2: Course Group Selection ===== -->
        <div class="coe-section-header mt-4">
          <i class="fas fa-book mr-2 text-success"></i>
          Course Group Selection
        </div>
        <hr class="coe-section-divider" />

        <b-row>
          <!-- Course Group -->
          <b-col cols="12" md="6">
            <b-form-group
              label="Course Group"
              label-class="font-weight-bold required-label"
            >
              <b-form-select
                v-model="form.course_group_id"
                :options="courseGroupOptions"
                :state="validated ? form.course_group_id !== null : null"
              />
              <b-form-invalid-feedback>Please select a course group.</b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- ===== SECTION 3: Eligibility Check ===== -->
        <div class="coe-section-header mt-4">
          <i class="fas fa-user-check mr-2 text-warning"></i>
          Eligibility Check
        </div>
        <hr class="coe-section-divider" />

        <b-row>
          <!-- Attendance Eligibility -->
          <b-col cols="12" md="4">
            <div class="coe-eligibility-card">
              <div class="coe-eligibility-title">Attendance</div>

              <b-form-checkbox v-model="form.eligibility.attendance.min_attendance_enabled">
                Minimum Attendance Percentage
                <i
                  class="fas fa-info-circle text-muted ml-1"
                  style="font-size: 13px !important; cursor: pointer;"
                  v-b-popover.hover.html.top="minAttendanceInfo"
                  title="Minimum Attendance Percentage"
                ></i>
              </b-form-checkbox>
              <b-form-group v-if="form.eligibility.attendance.min_attendance_enabled" class="ml-4 mt-2 mb-3">
                <b-form-input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  v-model.number="form.eligibility.attendance.min_attendance_percentage"
                  @blur="form.eligibility.attendance.min_attendance_percentage = clampPercent(form.eligibility.attendance.min_attendance_percentage)"
                  placeholder="e.g. 75"
                />
              </b-form-group>

              <b-form-checkbox v-model="form.eligibility.attendance.condonation_enabled">
                Attendance Condonation
                <i
                  class="fas fa-info-circle text-muted ml-1"
                  style="font-size: 13px !important; cursor: pointer;"
                  v-b-popover.hover.html.top="condonationInfo"
                  title="Attendance Condonation"
                ></i>
              </b-form-checkbox>
              <b-form-group v-if="form.eligibility.attendance.condonation_enabled" class="ml-4 mt-2 mb-0">
                <b-form-input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  v-model.number="form.eligibility.attendance.condonation_threshold"
                  @blur="form.eligibility.attendance.condonation_threshold = clampPercent(form.eligibility.attendance.condonation_threshold)"
                  placeholder="Condonation threshold"
                />
              </b-form-group>
            </div>
          </b-col>

          <!-- Internal Marks Eligibility -->
          <b-col cols="12" md="4">
            <div class="coe-eligibility-card">
              <div class="coe-eligibility-title">Internal Marks</div>

              <b-form-checkbox v-model="form.eligibility.internal_marks.cutoff_enabled">
                Internal Mark Cutoff Percentage
              </b-form-checkbox>
              <b-form-group v-if="form.eligibility.internal_marks.cutoff_enabled" class="ml-4 mt-2 mb-0">
                <b-form-input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  v-model.number="form.eligibility.internal_marks.cutoff_percentage"
                  @blur="form.eligibility.internal_marks.cutoff_percentage = clampPercent(form.eligibility.internal_marks.cutoff_percentage)"
                  placeholder="e.g. 40"
                />
              </b-form-group>
            </div>
          </b-col>

          <!-- Fee Eligibility -->
          <b-col cols="12" md="4">
            <div class="coe-eligibility-card">
              <div class="coe-eligibility-title">Fee Eligibility</div>
              <p class="text-muted small mb-0">Configuration coming soon.</p>
            </div>
          </b-col>
        </b-row>

        <!-- ===== SECTION 4: Evaluation ===== -->
        <div class="coe-section-header mt-4">
          <i class="fas fa-user-check mr-2 text-info"></i>
          Evaluation
        </div>
        <hr class="coe-section-divider" />

        <b-row>
          <b-col cols="12" md="6">
            <b-form-group
              label="Evaluation Type"
              label-class="font-weight-bold"
              description="Default for every course group in this exam — each group's evaluation type can still be changed individually later."
            >
              <b-form-radio-group
                v-model="form.default_evaluation_type"
                :options="[{ text: 'Single', value: 'single' }, { text: 'Double', value: 'double' }]"
              />
            </b-form-group>
          </b-col>
        </b-row>

      <!-- ===== SECTION 5: Result Publication ===== -->

        <div class="coe-section-header mt-4">
          <i class="fas fa-check-circle mr-2 text-success"></i>
          Result Publication
        </div>
        <hr class="coe-section-divider" />

        <!-- ===== Final Mark Split Up ===== -->

         <b-form-group label="Final Mark Split Up" label-class="font-weight-bold">
          <b-row>
            <b-col cols="12" md="4">
              <div class="coe-subsection-header mt-3 mb-3">
                End Exam Total
              </div>
              <b-form-input v-model="form.exam_total" type="number" min="0" placeholder="Enter end exam total" />
            </b-col>

            <b-col cols="12" md="4">
              <div class="coe-subsection-header mt-3 mb-3">
                Internal Mark Total
              </div>
              <b-form-input v-model="form.internal_mark_total" type="number" min="0"
                placeholder="Enter internal mark total" />
            </b-col>

            <b-col cols="12" md="4">
              <div class="coe-subsection-header mt-3 mb-3">
                Final Mark Total
              </div>
              <b-form-input :value="finalMarkTotal" type="number" readonly />
            </b-col>
          </b-row>
        </b-form-group>
       <!-- ===== Publish Result ===== -->

       <b-form-group label="Publish Result" label-class="font-weight-bold">
          <b-row>
            <b-col cols="12">
              <b-form-radio-group v-model="form.result_publication_type" :options="[
                { text: 'Mark', value: 'mark' },
                { text: 'Grade', value: 'grade' }
              ]" />
            </b-col>
          </b-row>

          <b-row v-if="form.result_publication_type === 'grade'">
            <b-col cols="12">
              <b-button variant="info" size="sm" @click="addGradeField">
                + Add Grade
              </b-button>

              <div v-for="(grade, gradeIndex) in grades" :key="gradeIndex" class="marks mt-2 mb-2">
                <div class="d-flex align-items-center">

                  <b-form-input v-model="grade.min_mark" type="number" min="0" placeholder="Minimum Mark"
                    @input="validateGrades" />

                  <span class="mx-2">-</span>

                  <b-form-input v-model="grade.max_mark" type="number" min="0" placeholder="Maximum Mark"
                    @input="validateGrades" />

                  <b-form-input v-model="grade.grade" class="ml-2" placeholder="Grade" />

                  <span class="text-danger ml-2 btn m-0 p-0" @click="removeGradeField(gradeIndex)">
                    <i class="fas fa-minus-circle"></i>
                  </span>

                </div>

                <div v-if="grade.error" class="input-error-message">
                  {{ grade.error }}
                </div>
              </div>
            </b-col>
          </b-row>
        </b-form-group>

        <!-- ===== Date of Exam Publish ===== -->
        <b-row>
          <b-col cols="12" md="6">
            <b-form-group label="Date of Exam Publish" label-class="font-weight-bold">
              <b-form-input type="date" v-model="form.exam_publish_date" />
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Action buttons -->
        <div class="d-flex justify-content-end mt-3" style="gap: 8px;">
          <b-button
            variant="secondary"
            size="sm"
            @click="$emit('close')"
            :disabled="submitting"
          >
            Cancel
          </b-button>
          <b-button
            variant="primary"
            size="sm"
            type="submit"
            :disabled="submitting"
          >
            <b-spinner small v-if="submitting" class="mr-1" />
            <i v-else class="fas fa-save mr-1"></i>
            {{ submitting ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Exam' : 'Create Exam') }}
          </b-button>
        </div>

      </div>
    </b-form>
  </div>
</template>

<script>
export default {
  name: "CoeExamCreateForm",

  props: {
    exam: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      fetchDropdowns: false,
      submitting: false,
      validated: false,
      errorMessage: "",

      classOptions: [
        { value: null, text: "Select class", disabled: true },
        ...["S1","S2","S3","S4","S5","S6","S7","S8","S9","S10"].map(s => ({ value: s, text: s })),
      ],
      schemeOptions: [
        { value: null, text: "Select scheme", disabled: true },
        ...((() => { const opts = []; for (let y = 2026; y >= 2012; y--) opts.push({ value: String(y), text: String(y) }); return opts; })()),
      ],
      courseGroupOptions: [{ value: null, text: "Select course group", disabled: true }],

      minAttendanceInfo:
        "Attendance shortage condonation is a formal relaxation granted by the institution that allows a student to appear for an exam even if they haven't met the minimum attendance requirement.",
      condonationInfo:
        "Attendance condonation threshold is the minimum attendance limit below which condonation is not allowed.",

      examTypeOptions: [
        { value: null, text: "Select exam type", disabled: true },
        { value: "REGULAR", text: "Regular" },
        { value: "SUPPLEMENTARY", text: "Supplementary" },
        { value: "IMPROVEMENT", text: "Improvement" },
      ],

      form: {
        exam_name: "",
        exam_type: null,
        class_id: null,
        scheme: null,
        academic_year: null,
        exam_period: "",
        exam_publish_date: "",
        course_group_id: null,
        default_evaluation_type: "single",

        exam_total: null,
        internal_mark_total: null,
        result_publication_type: 'mark',

        eligibility: {
          attendance: {
            min_attendance_enabled: false,
            min_attendance_percentage: null,
            condonation_enabled: false,
            condonation_threshold: null,
          },
          internal_marks: {
            cutoff_enabled: false,
            cutoff_percentage: null,
          },
        },
      },
      grades: [],
    };
  },

  computed: {
    isEditMode() {
      return !!this.exam;
    },
    finalMarkTotal() {
      return (
        Number(this.form.exam_total || 0) +
        Number(this.form.internal_mark_total || 0)
      );
    },
    academicYearSemesterOptions() {
      return this.$settingAcedemicYearsAndSemester.filter(
        y => y.value === null || (Array.isArray(y.value) && y.value[0] !== "0")
      );
    },
  },

  async created() {
    this.fetchDropdowns = true;
    try {
      await this.loadCourseGroups();
    } finally {
      this.fetchDropdowns = false;
    }

    if (this.isEditMode) {
      this.prefillFromExam();
    }
  },

  methods: {
    prefillFromExam() {
      const exam = this.exam;
      const resultPublication = exam.result_publication || {};

      this.form.exam_name = exam.exam_name || "";
      this.form.exam_type = exam.exam_type;
      this.form.class_id = exam.class_id;
      this.form.scheme = exam.scheme;
      this.form.academic_year = [exam.academic_year, exam.academic_semester];
      // Stored as "YYYY-MM-01"; the month input needs "YYYY-MM".
      this.form.exam_period = (exam.exam_period || "").slice(0, 7);
      this.form.exam_publish_date = (exam.exam_publish_date || "").slice(0, 10);
      this.form.course_group_id = exam.course_group
        ? exam.course_group.id
        : exam.course_group_id;
      this.form.default_evaluation_type = exam.default_evaluation_type || "single";

      this.form.exam_total = resultPublication.exam_total ?? null;
      this.form.internal_mark_total = resultPublication.internal_mark_total ?? null;
      this.form.result_publication_type = resultPublication.publication_type || "mark";
      this.grades = (resultPublication.grades || []).map(grade => ({
        min_mark: grade.min_mark,
        max_mark: grade.max_mark,
        grade: grade.grade,
        error: null,
      }));

      const attendance = (exam.eligibility && exam.eligibility.attendance) || {};
      const internalMarks = (exam.eligibility && exam.eligibility.internal_marks) || {};
      this.form.eligibility.attendance.min_attendance_enabled = !!attendance.min_attendance_enabled;
      this.form.eligibility.attendance.min_attendance_percentage = attendance.min_attendance_percentage ?? null;
      this.form.eligibility.attendance.condonation_enabled = !!attendance.condonation_enabled;
      this.form.eligibility.attendance.condonation_threshold = attendance.condonation_threshold ?? null;
      this.form.eligibility.internal_marks.cutoff_enabled = !!internalMarks.cutoff_enabled;
      this.form.eligibility.internal_marks.cutoff_percentage = internalMarks.cutoff_percentage ?? null;
    },

    addGradeField() {
      this.grades.push({
        min_mark: null,
        max_mark: null,
        grade: null,
        error: null,
      });
    },

    removeGradeField(index) {
      this.grades.splice(index, 1);
      this.validateGrades();
    },

    validateGrades() {
      // Clear previous errors
      this.grades.forEach((grade) => {
        grade.error = null;
      });

      // Check every grade against every other grade
      for (let i = 0; i < this.grades.length; i++) {
        const current = this.grades[i];

        if (
          current.min_mark === null ||
          current.min_mark === '' ||
          current.max_mark === null ||
          current.max_mark === ''
        ) {
          continue;
        }

        const currentMin = Number(current.min_mark);
        const currentMax = Number(current.max_mark);

        for (let j = 0; j < i; j++) {
          const previous = this.grades[j];

          if (
            previous.min_mark === null ||
            previous.min_mark === '' ||
            previous.max_mark === null ||
            previous.max_mark === ''
          ) {
            continue;
          }

          const previousMin = Number(previous.min_mark);
          const previousMax = Number(previous.max_mark);

          if (
            currentMin <= previousMax &&
            currentMax >= previousMin
          ) {
            current.error =
              `Mark range overlaps with Grade ${j + 1} (${previousMin}-${previousMax}).`;

            return false;
          }
        }
      }

      return true;
    },

    clampPercent(value) {
      if (value === null || value === undefined || value === "" || isNaN(value)) return value;
      return Math.min(100, Math.max(0, value));
    },

    async loadCourseGroups() {
      this.errorMessage = "";
      try {
        const response = await this.$axios.get(
          `${this.$store.getters.getAPIKey.mainAPI}/course-groups`
        );
        const data = response.data.data || [];
        this.courseGroupOptions = [
          { value: null, text: "Select course group", disabled: true },
          ...data.map((item) => ({ value: item.id, text: item.name })),
        ];
      } catch {
        this.errorMessage = "Failed to load course groups.";
      }
    },

    isFormValid() {
      const f = this.form;
      const attendance = f.eligibility.attendance;
      const internalMarks = f.eligibility.internal_marks;

      const resultPublicationValid =
        f.exam_total !== null &&
        f.exam_total !== "" &&
        Number(f.exam_total) >= 0 &&
        f.internal_mark_total !== null &&
        f.internal_mark_total !== "" &&
        Number(f.internal_mark_total) >= 0 &&
        ["mark", "grade"].includes(f.result_publication_type);

      return (
        f.exam_name.trim().length > 0 &&
        f.exam_type !== null &&
        f.class_id !== null &&
        f.scheme !== null &&
        f.academic_year !== null &&
        !!f.exam_period &&
        f.course_group_id !== null &&

        (!attendance.min_attendance_enabled ||
          (
            attendance.min_attendance_percentage !== null &&
            attendance.min_attendance_percentage >= 0 &&
            attendance.min_attendance_percentage <= 100
          )) &&

        (!attendance.condonation_enabled ||
          (
            attendance.condonation_threshold !== null &&
            attendance.condonation_threshold >= 0 &&
            attendance.condonation_threshold <= 100
          )) &&

        (!internalMarks.cutoff_enabled ||
          (
            internalMarks.cutoff_percentage !== null &&
            internalMarks.cutoff_percentage >= 0 &&
            internalMarks.cutoff_percentage <= 100
          )) &&

        resultPublicationValid
      );
    },

    parsePeriod(period) {
      const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
      const [year, month] = period.split("-");
      return { exam_month: months[parseInt(month, 10) - 1], exam_year: parseInt(year, 10) };
    },

    async submitForm() {
      this.validated = true;

      if (!this.isFormValid()) return;

      if (
        this.form.result_publication_type === "grade" &&
        !this.validateGrades()
      ) {
        return;
      }

      this.submitting = true;
      this.errorMessage = "";

      try {
        const payload = {
          exam_name: this.form.exam_name.trim(),
          exam_type: this.form.exam_type,
          class_id: this.form.class_id,
          scheme: this.form.scheme,
          academic_year: this.form.academic_year[0],
          academic_semester: this.form.academic_year[1],
          exam_period: this.form.exam_period,
          exam_publish_date: this.form.exam_publish_date || null,
          course_group_id: this.form.course_group_id,
          default_evaluation_type: this.form.default_evaluation_type,

          eligibility: this.form.eligibility,

          result_publication: {
            exam_total: Number(this.form.exam_total || 0),
            internal_mark_total: Number(this.form.internal_mark_total || 0),
            final_mark_total: this.finalMarkTotal,
            publication_type: this.form.result_publication_type,
            grades: this.form.result_publication_type === "grade"
              ? this.grades.map(grade => ({
                min_mark: Number(grade.min_mark),
                max_mark: Number(grade.max_mark),
                grade: grade.grade
              }))
              : []
          }
        };

        const mainAPI = this.$store.getters.getAPIKey.mainAPI;
        if (this.isEditMode) {
          await this.$axios.put(`${mainAPI}/end-sem-exams/${this.exam.id}`, payload);

          this.$toast.success("End semester exam updated successfully.", {
            position: "top",
            duration: 3000,
          });

          this.$emit("updated");
        } else {
          await this.$axios.post(`${mainAPI}/end-sem-exams`, payload);

          this.$toast.success("End semester exam created successfully.", {
            position: "top",
            duration: 3000,
          });

          this.$emit("created");
        }
      } catch (error) {
        this.errorMessage =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          (this.isEditMode ? "Failed to update exam." : "Failed to create exam.");
      } finally {
        this.submitting = false;
      }
    }
  },
};
</script>

<style scoped>
.coe-form-card {
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  max-width: 50%;
  margin: 0 auto;
}

.coe-section-header {
  font-size: 1rem;
  font-weight: 600;
  color: #333;
}

.coe-section-divider {
  border-color: #dee2e6;
  margin-top: 6px;
  margin-bottom: 16px;
}

.required-label::after {
  content: " *";
  color: #dc3545;
}

.coe-eligibility-card {
  background: #fafafa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 14px;
  height: 100%;
}

.coe-eligibility-title {
  font-weight: 600;
  font-size: 0.9rem;
  color: #333;
  margin-bottom: 10px;
}
</style>
